import {
  useMemo,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';

import { Spacer } from 'theSource/components';
import { FormFieldsCategory } from 'theSource/types';
import {
  ENROLL_IN_MEDICARE_FIELD_NAME,
  LIVES_AT_HOME_FIELD_NAME,
  VERIFIED_FIELD_NAME,
} from 'theSource/features/FamilyMembers/utils';
import { selectIsAdmin } from 'theSource/features';
import { getFilteredCategories } from 'theSource/features/FamilyMembers/components/FamilyMemberForm/utils';
import { ADDRESS_FIELDS } from 'theSource/features/FamilyMembers/components/FamilyMemberForm/utils/constants';
import { useEnrollmentProfileQuery } from 'enrollment/queries/useEnrollmentProfileQuery';
import { FormElement } from 'enrollment/components/Shared/FormElement/';
import { ToggleBooleanState } from 'components/ToggleBooleanState';
import { RequiredLegend } from 'enrollment/features/Shared/RequiredLegend/RequiredLegend';
import autocompleteValues from 'theSource/utils/fieldUtils/autocomplete.json';
import { dependentsFormFields } from './DependentDetailsHelper';

declare const bootstrap: any;

export interface FamilyMemberFormProps {
  dependentId?: any;
  categories: FormFieldsCategory[];
  values?: { [key: string]: any };
  handleChange?: (value: any) => void;
  onFormValidityChange?: (isValid: boolean) => void;
  formType?: string; // new or edit
  controlled?: boolean;
  isCropEnabled?: boolean;
}

interface SubscriberData {
  [key: string]: { value: string };
}

export interface FamilyMemberFormRef {
  submitForm: () => void;
  getSubmissionObject: () => { [key: string]: any };
}

export const FamilyMemberForm = forwardRef<
  FamilyMemberFormRef,
  FamilyMemberFormProps
>(
  (
    {
      dependentId,
      categories,
      values,
      handleChange,
      onFormValidityChange,
      formType,
      controlled = false,
      isCropEnabled,
    },
    ref
  ) => {
    const isAdmin = useSelector(selectIsAdmin);
    const [livesAtHome, setLivesAtHome] = useState(
      Boolean(values?.[LIVES_AT_HOME_FIELD_NAME])
    );
    const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled: true });
    // Memoize subscriberData
    const subscriberData = useMemo(() => {
      return (subscriber?.data as SubscriberData) || {};
    }, [subscriber]);

    const isEnrolledInMedicare = Boolean(
      values?.[ENROLL_IN_MEDICARE_FIELD_NAME]
    );
    const isVerified = Boolean(values?.[VERIFIED_FIELD_NAME]);

    const filteredCategories = useMemo(() => {
      return getFilteredCategories(
        categories || [],
        false, // we don't want to hide address fields if livesAtHome is true for this form
        isEnrolledInMedicare,
        isVerified
      );
    }, [categories, isEnrolledInMedicare, isVerified]);

    const medicareFields = useMemo(() => {
      const items = (categories || [])
        .filter((category) => category.name === 'Other')
        .flatMap((category) => category.fields)
        .filter((field) =>
          [
            'medicare_effective_date',
            'medicare_plan',
            'medicare_reason',
            'medicare_termination_date',
          ].includes(field.fieldName)
        );

      return items;
    }, [categories]);

    const [addressFieldsDisabled, setAddressFieldsDisabled] = useState(false);
    const [formInteracted, setFormInteracted] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);
    const [initialValues, setInitialValues] = useState<{ [key: string]: any }>(
      {}
    );
    const [currentValues, setCurrentValues] = useState<{ [key: string]: any }>(
      {}
    );
    const [changedFields, setChangedFields] = useState<{
      [key: string]: boolean;
    }>({});
    const [fieldsValidity, setFieldsValidity] = useState<{
      [key: string]: boolean | undefined;
    }>({});

    const [isMedicareFieldsPanel, setIsMedicareFieldsPanel] =
      ToggleBooleanState(false) as [boolean, (value?: boolean) => void];

    // Store initial values on first render
    useEffect(() => {
      const initial = filteredCategories.reduce(
        (acc, category) => {
          category.fields.forEach((field) => {
            acc[field.fieldName] = values?.[field.fieldName] || '';
          });
          return acc;
        },
        {} as { [key: string]: any }
      );
      setInitialValues(initial);
      setCurrentValues(initial);
    }, [filteredCategories, values]);

    // Update handleChange to set formInteracted to true and check form validity
    const handleChangeWithInteraction = (event: any) => {
      const { name, value, type, checked } = event.target;
      const newValue = type === 'checkbox' ? checked : value;
      if (currentValues[name] !== newValue) {
        setCurrentValues((prevValues) => ({
          ...prevValues,
          [name]: newValue,
        }));
        if (handleChange) {
          handleChange(event);
        }
        setFormInteracted(true);
        checkFormValidity();
        setChangedFields((prev) => ({
          ...prev,
          [name]: true,
        }));
        // If the field is required, check its validity
        if (initialValues[name] === '' || newValue === '') {
          const fieldElement = document.getElementById(
            name
          ) as HTMLInputElement;
          if (fieldElement) {
            const isValid = fieldElement.checkValidity();
            setFieldsValidity((prev) => ({ ...prev, [name]: isValid }));
          }
        } else {
          setFieldsValidity((prev) => ({ ...prev, [name]: true }));
        }
      }
    };

    // Function to check form validity using checkValidity
    const checkFormValidity = () => {
      if (formRef.current) {
        const allRequiredFieldsFilled =
          formType === 'new'
            ? filteredCategories
              .flatMap((category) => category.fields)
              .filter((field) =>
                field.required &&
                field.fieldName !== 'ssn' &&
                field.fieldName !== 'sin'
              )
              .every((field) => {
                const fieldElement = document.getElementById(
                  field.fieldName
                ) as HTMLInputElement;
                return fieldElement && fieldElement.checkValidity();
              })
            : true;
        const allValid = Object.values(fieldsValidity).every((valid) => valid);
        const anyChanged = Object.values(changedFields).some(
          (changed) => changed
        );
        if (onFormValidityChange) {
          onFormValidityChange(
            allRequiredFieldsFilled && allValid && anyChanged
          );
        }
      }
    };

    // LIVES_AT_HOME
    type AddressFields = {
      [key: string]: string;
    };

    const [formValues, setFormValues] = useState<AddressFields>(
      ADDRESS_FIELDS.reduce<AddressFields>(
        (values, field) => ({
          ...values,
          [field]: '',
        }),
        {}
      )
    );

    // Update address fields and disabled state when livesAtHome changes
    useEffect(() => {
      const updatedFormValues = { ...formValues };

      if (livesAtHome && subscriber) {
        // Populate address fields from subscriber data
        // const subscriberData = subscriber.data as SubscriberData;
        ADDRESS_FIELDS.forEach((field) => {
          updatedFormValues[field] = subscriberData[field]?.value || '';
          handleValidityChange(true, field); // Mark field as valid when livesAtHome is true
        });

        setAddressFieldsDisabled(true);
      } else {
        // Explicitly remove selected attribute from all select fields
        filteredCategories.forEach((category) => {
          category.fields.forEach((field) => {
            if (ADDRESS_FIELDS.includes(field.fieldName)) {
              updatedFormValues[field.fieldName] = ''; // Reset to empty string
              if (field.options) {
                const selectElement = document.getElementById(
                  field.fieldName
                ) as HTMLSelectElement;
                if (selectElement) {
                  selectElement.selectedIndex = 0; // Select the first option
                }
              }
            }
          });
        });

        setAddressFieldsDisabled(false);
      }

      setFormValues(updatedFormValues);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [livesAtHome, subscriber]);

    useEffect(() => {
      checkFormValidity();
      // eslint-disable-next-line
    }, [formValues]);

    // Used with Edit Member form
    useEffect(() => {
      if (formType === 'edit' && !formInteracted) {
        if (livesAtHome && subscriber) {
          setAddressFieldsDisabled(true);
        } else {
          setAddressFieldsDisabled(false);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formInteracted, formType, livesAtHome, subscriber]);

    // Used with New Member validation OR once Edit Member form is interacted with
    useEffect(() => {
      if (formType === 'new' || formInteracted) {
        checkFormValidity();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formInteracted, formType, livesAtHome, subscriber, fieldsValidity]);

    useEffect(() => {
      if (isEnrolledInMedicare) {
        const collapseElement = document.querySelector('#medicareFields');
        const collapseMedicareFields = new bootstrap.Collapse(collapseElement, {
          toggle: false,
        });

        if (isEnrolledInMedicare) {
          setIsMedicareFieldsPanel(true);
          collapseMedicareFields.show();
        } else {
          setIsMedicareFieldsPanel(false);
          collapseMedicareFields.hide();
        }
      }
    }, [
      filteredCategories,
      isEnrolledInMedicare,
      categories,
      setIsMedicareFieldsPanel,
    ]);

    // Function to handle form submission and create submission object
    const handleSubmit = () => {
      // eslint-disable-next-line
      const submissionObject = Object.keys(currentValues).reduce(
        (acc, key) => {
          if (currentValues[key] !== initialValues[key]) {
            acc[key] = currentValues[key];
          }
          return acc;
        },
        {} as { [key: string]: any }
      );
    };

    const getSubmissionObject = () => {
      return Object.keys(currentValues).reduce(
        (acc, key) => {
          if (currentValues[key] !== initialValues[key]) {
            acc[key] = currentValues[key];
          }
          return acc;
        },
        {} as { [key: string]: any }
      );
    };

    // Expose the submitForm and getSubmissionObject methods to parent components
    useImperativeHandle(ref, () => ({
      submitForm: handleSubmit,
      getSubmissionObject,
    }));

    const handleValidityChange = (isValid: boolean, fieldId: string) => {
      const isSSN = fieldId === 'ssn' || fieldId === 'sin';
      setFieldsValidity((prev) => ({ ...prev, [fieldId]: isValid || isSSN }));
      checkFormValidity();
    };

    const handleFieldChange = (fieldId: string, newValue: any) => {
      const initialValue = initialValues[fieldId];
      const hasChanged = initialValue !== newValue;
      setChangedFields((prev) => ({ ...prev, [fieldId]: hasChanged }));

      // Check validity after change
      const fieldElement = document.getElementById(fieldId) as HTMLInputElement;
      if (fieldElement) {
        const isValid = fieldElement.checkValidity();
        setFieldsValidity((prev) => ({ ...prev, [fieldId]: isValid }));
      }
    };

    const handleShowMedicareFields = () => {
      const collapseElement = document.querySelector('#medicareFields');
      const collapseMedicareFields = new bootstrap.Collapse(collapseElement, {
        toggle: false,
      });

      if (isMedicareFieldsPanel) {
        setIsMedicareFieldsPanel(false);
        collapseMedicareFields.hide();
      } else {
        setIsMedicareFieldsPanel(true);
        collapseMedicareFields.show();
      }
    };

    return (
      <Box
        data-testid="enrollment-family-member-form"
        id={`enrollment-family-member-form-${dependentId}`}
        component="form"
        ref={formRef}
      >
        <RequiredLegend />
        {filteredCategories.map((category: any, index: number) => {

          const filteredFields = category.fields.filter(
            (field: any) => field.fieldName && dependentsFormFields.includes(field.fieldName)
          );

          return (
            <div className="row mb-5" key={index}>
              <h3 className="mx-2">{category.name}</h3>
              {filteredFields.map((field: any, index: number) => {
                const isFieldDisabled = ADDRESS_FIELDS.includes(field.fieldName)
                  ? addressFieldsDisabled
                  : Boolean(
                    (formValues as { [key: string]: any })[field.fieldName]
                  );

                const finalFormField =
                  Array.isArray(field.options) && field.options.length > 0
                    ? 'select'
                    : field.dataType.toLowerCase();

                // Dynamically fetch autocomplete value based on the field's state
                const autocompleteValue = isFieldDisabled
                  ? 'off' // Set autocomplete to "off" if the field is disabled
                  : autocompleteValues[
                  field.fieldName as keyof typeof autocompleteValues
                  ] || 'on';

                let finalValue;
                switch (field.fieldName) {
                  case 'birthdate':
                    if ((formValues as { [key: string]: any })[field.fieldName]) {
                      finalValue = new Date(
                        (formValues as { [key: string]: any })[field.fieldName]
                      )
                        .toISOString()
                        .split('T')[0];
                    } else if (field.value) {
                      finalValue = new Date(field.value)
                        .toISOString()
                        .split('T')[0];
                    } else {
                      finalValue = '';
                    }
                    break;
                  default:
                    finalValue =
                      (formValues as { [key: string]: any })[field.fieldName] ||
                      field.value;
                    break;
                }

                if (field.fieldName === 'lives_at_home') {
                  // For LIVES_AT_HOME checkbox
                  return (
                    <div className="col-12" key={index}>
                      <FormElement
                        formField="checkbox"
                        inputId={field.fieldName}
                        label={field.label}
                        name={field.fieldName}
                        required={field.required}
                        detectChange={setLivesAtHome}
                        disabled={isCropEnabled || isFieldDisabled}
                        checked={livesAtHome}
                        value={finalValue}
                        controlled={controlled}
                        onValidityChange={(isValid) =>
                          handleValidityChange(isValid, field.fieldName)
                        }
                        autocomplete={autocompleteValue} // Add the autocomplete attribute
                        onInput={(e: any) => {
                          const target = e.target as HTMLInputElement; // Type assertion
                          const value =
                            target.type === 'checkbox'
                              ? target.checked
                              : target.value;
                          handleFieldChange(field.fieldName, value);
                          handleChangeWithInteraction(e);
                        }}
                      />
                    </div>
                  );
                } else if (!field.fieldName.toLowerCase().includes('medicare')) {
                  return (
                    // For other fields
                    <div className="col-12" key={index}>
                      <FormElement
                        formField={finalFormField}
                        inputId={field.fieldName}
                        label={field.label}
                        name={field.fieldName}
                        options={field.options}
                        required={field.required}
                        value={finalValue}
                        checked={field.value}
                        controlled={controlled}
                        disabled={
                          isCropEnabled || isFieldDisabled || !field.editable
                        }
                        isInvalid={
                          fieldsValidity[field.fieldName] === false
                            ? true
                            : undefined
                        }
                        onValidityChange={(isValid) =>
                          handleValidityChange(isValid, field.fieldName)
                        }
                        autocomplete={autocompleteValue} // Add the autocomplete attribute
                        onInput={(e: any) => {
                          handleFieldChange(field.fieldName, e.target.value);
                          handleChangeWithInteraction(e);
                        }}
                      />
                    </div>
                  );
                } else if (
                  field.fieldName === 'enroll_in_medicare' &&
                  medicareFields.length !== 0
                ) {
                  // For ENROLL_IN_MEDICARE checkbox
                  return (
                    <>
                      <div className="col-12 mt-4" key={index}>
                        <FormElement
                          formField="checkbox"
                          inputId={field.fieldName}
                          label={field.label}
                          name={field.fieldName}
                          required={field.required}
                          disabled={!isAdmin}
                          checked={isEnrolledInMedicare}
                          value={finalValue}
                          controlled={controlled}
                          onValidityChange={(isValid) =>
                            handleValidityChange(isValid, field.fieldName)
                          }
                          autocomplete={autocompleteValue} // Add the autocomplete attribute
                          onInput={(e: any) => {
                            const target = e.target as HTMLInputElement; // Type assertion
                            const value = target.checked;
                            setCurrentValues((prevValues) => ({
                              ...prevValues,
                              [field.fieldName]: value,
                            }));
                            handleFieldChange(field.fieldName, value);
                            handleChangeWithInteraction(e);
                            handleShowMedicareFields();
                          }}
                        />
                      </div>
                      {/* Medicare Collapsible Section */}
                      <section className="collapse w-100" id="medicareFields">
                        {medicareFields.map(
                          (medicareField: any, index: number) => {
                            const finalMedicareField =
                              Array.isArray(medicareField.options) &&
                                medicareField.options.length > 0
                                ? 'select'
                                : medicareField.dataType.toLowerCase();
                            let finalMedicareValue;
                            switch (medicareField.fieldName) {
                              case 'medicare_effective_date':
                              case 'medicare_termination_date':
                                if (
                                  (formValues as { [key: string]: any })[
                                  medicareField.fieldName
                                  ]
                                ) {
                                  finalMedicareValue = new Date(
                                    (formValues as { [key: string]: any })[
                                    medicareField.fieldName
                                    ]
                                  )
                                    .toISOString()
                                    .split('T')[0];
                                } else if (medicareField.value) {
                                  finalMedicareValue = new Date(
                                    medicareField.value
                                  )
                                    .toISOString()
                                    .split('T')[0];
                                } else {
                                  finalMedicareValue = '';
                                }
                                break;
                              default:
                                finalMedicareValue =
                                  (formValues as { [key: string]: any })[
                                  medicareField.fieldName
                                  ] || medicareField.value;
                                break;
                            }
                            return (
                              <div className="col-12" key={index}>
                                <FormElement
                                  formField={finalMedicareField}
                                  inputId={medicareField.fieldName}
                                  label={medicareField.label}
                                  name={medicareField.fieldName}
                                  options={medicareField.options}
                                  required={medicareField.required}
                                  value={finalMedicareValue}
                                  controlled={controlled}
                                  disabled={
                                    isCropEnabled ||
                                    isFieldDisabled ||
                                    !medicareField.editable
                                  }
                                  autocomplete={autocompleteValue} // Add the autocomplete attribute
                                  isInvalid={
                                    fieldsValidity[medicareField.fieldName] ===
                                      false
                                      ? true
                                      : undefined
                                  }
                                  onValidityChange={(isValid) =>
                                    handleValidityChange(
                                      isValid,
                                      medicareField.fieldName
                                    )
                                  }
                                  onInput={(e: any) => {
                                    handleFieldChange(
                                      medicareField.fieldName,
                                      e.target.value
                                    );
                                    handleChangeWithInteraction(e);
                                  }}
                                />
                              </div>
                            );
                          }
                        )}
                      </section>
                    </>
                  );
                }
                return null;
              })}
              <Spacer height={8} />
            </div>
          );
        })}
      </Box>
    );
  }
);
